// 3-column docs layout route: left page-tree nav, center <Outlet/>, right
// "On this page" scroll-spy TOC. Reuses SiteNav + BigFooter + the sub-page
// atmosphere. Left nav collapses into a slide-in drawer on mobile; the right
// TOC is dropped below ~1100px.
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { SiteNav } from '../sections/hero';
import { BigFooter, DISCORD_INVITE } from '../sections/footer';
import { CRWIcon } from '../components/icons';
import { DOCS, pageForPath, categoryForPage, type TocItem } from './registry';
import { DOCS_ANCHOR_OFFSET, useScrollSpy } from './useScrollSpy';

function DocTree({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const active = pageForPath(pathname);
  const activeCatId = active ? categoryForPage(active)?.id : undefined;

  // Accordion: only the active section is expanded by default, so all nine
  // section headers stay visible and the current page is never clipped below
  // the fold of the (scrollable) tree.
  const [open, setOpen] = useState<Set<string>>(() => new Set([activeCatId ?? DOCS[0].id]));
  useEffect(() => {
    if (activeCatId) setOpen(new Set([activeCatId]));
  }, [activeCatId]);
  useEffect(() => {
    const el = document.querySelector('.crw-docs3-tree [aria-current="page"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [pathname]);
  const toggle = (id: string) => setOpen((prev) => {
    const nxt = new Set(prev);
    if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
    return nxt;
  });

  return (
    <nav aria-label="Documentation" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {DOCS.map((cat) => {
        const isOpen = open.has(cat.id);
        return (
          <div key={cat.id} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button
              onClick={() => toggle(cat.id)}
              aria-expanded={isOpen}
              className="crw-docnav-toggle"
            >
              <span className="cr-label" style={{ color: 'var(--fg-2)' }}>{cat.label}</span>
              <CRWIcon d="chevdown" size={12} style={{ color: 'var(--fg-4)', transition: 'transform 180ms var(--ease-out)', transform: isOpen ? 'none' : 'rotate(-90deg)' }} />
            </button>
            {/* accordion: content stays mounted so the height collapses/expands
                smoothly (grid-template-rows 0fr<->1fr) instead of an instant
                show/hide; inert removes the clipped links from tab order while closed */}
            <div className={`crw-docnav-collapse${isOpen ? ' crw-docnav-collapse--open' : ''}`}>
              <div className="crw-docnav-collapse-inner" inert={!isOpen}>
                {cat.pages.map((p) => (
                  <NavLink
                    key={p.slug || 'index'}
                    to={p.slug === '' ? '/docs' : `/docs/${p.slug}`}
                    end
                    onClick={onNavigate}
                    className={({ isActive }) => `crw-docnav-item${isActive ? ' crw-docnav-item--active' : ''}`}
                  >
                    {p.title}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        );
      })}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line-1)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="crw-docnav-utility" style={{ fontSize: 12.5, textDecoration: 'none' }}>Stuck? Ask on Discord →</a>
        <NavLink to="/download" className="crw-docnav-utility" style={{ fontSize: 12.5, textDecoration: 'none' }}>Download ClipRadar →</NavLink>
      </div>
    </nav>
  );
}

function OnThisPage({ toc }: { toc: TocItem[] }) {
  const active = useScrollSpy(toc.map((t) => t.id));
  if (!toc.length) return null;
  const go = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - DOCS_ANCHOR_OFFSET, behavior: 'smooth' });
  };
  return (
    <nav aria-label="On this page" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="cr-label" style={{ marginBottom: 8 }}>On this page</span>
      {toc.map((t) => {
        const on = active === t.id;
        return (
          <a
            key={t.id}
            href={`#${t.id}`}
            onClick={(e) => go(e, t.id)}
            className={`crw-doctoc-item${on ? ' crw-doctoc-item--active' : ''}`}
            style={{ padding: '5px 10px', borderRadius: 7, textDecoration: 'none', fontSize: 12.5, lineHeight: 1.4 }}
          >
            {t.label}
          </a>
        );
      })}
    </nav>
  );
}

export function DocsShell() {
  const { pathname } = useLocation();
  const page = pageForPath(pathname);
  const toc = page?.toc ?? [];
  const [drawer, setDrawer] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const drawerTrigger = useRef<Element | null>(null);

  // close the drawer on route change
  useEffect(() => { setDrawer(false); }, [pathname]);
  // lock body scroll while the drawer is open
  useEffect(() => {
    if (!drawer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawer]);
  // a11y: Esc closes the drawer; focus moves into it on open and back to the
  // trigger on close (lighter than the modal's full focus trap, but no dead ends)
  useEffect(() => {
    if (!drawer) return;
    drawerTrigger.current = document.activeElement;
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      (drawerTrigger.current as HTMLElement | null)?.focus?.();
    };
  }, [drawer]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />

      {/* mobile sub-bar: opens the left-nav drawer */}
      <div className="crw-docs-subbar">
        <button className="crw-docs-menubtn" onClick={() => setDrawer(true)} aria-label="Open documentation menu">
          <CRWIcon d="menu" size={16} />
          <span>Contents</span>
        </button>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page?.title ?? 'Documentation'}</span>
      </div>

      <main style={{ flex: 1, position: 'relative', overflow: 'hidden', padding: '120px 24px 96px' }}>
        {/* atmosphere — blueprint grid + one soft signal glow (same as PageShell) */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, black, transparent 520px)', WebkitMaskImage: 'linear-gradient(180deg, black, transparent 520px)' }} />
          <div style={{ position: 'absolute', top: -260, left: '50%', transform: 'translateX(-50%)', width: 900, height: 520, background: 'radial-gradient(ellipse at center, rgba(74,227,255,0.07), rgba(139,124,255,0.03) 50%, transparent 70%)', filter: 'blur(30px)' }} />
        </div>

        <div style={{ position: 'relative', maxWidth: 1300, margin: '0 auto' }}>
          <div className={`crw-docs3${toc.length ? '' : ' crw-docs3--notoc'}`}>
            <aside className="crw-docs3-tree">
              <DocTree />
            </aside>
            <div style={{ minWidth: 0 }}>
              <Outlet />
            </div>
            {toc.length ? (
              <aside className="crw-docs3-toc">
                <OnThisPage toc={toc} />
              </aside>
            ) : null}
          </div>
        </div>
      </main>

      {/* mobile drawer */}
      {drawer ? (
        <>
          <div className="crw-docs-drawer-backdrop" onClick={() => setDrawer(false)} />
          <div className="crw-docs-drawer" role="dialog" aria-modal="true" aria-label="Documentation menu" ref={drawerRef} tabIndex={-1}>
            <DocTree onNavigate={() => setDrawer(false)} />
          </div>
        </>
      ) : null}

      <BigFooter />
    </div>
  );
}
